import { useEffect, useRef, useState } from "react";

/**
 * Open/close state + dismissal wiring for a hand-rolled popover/dropdown menu
 * (the project has no dropdown-menu library). While `open`, a pointer-down
 * outside `containerRef` OR an Escape keypress closes the menu; both listeners
 * are registered only while open and torn down on close.
 *
 * Attach `containerRef` to the wrapper that holds BOTH the trigger button and
 * the panel, so clicking the trigger to toggle doesn't count as "outside".
 *
 * Shared by ExportResultsMenu + RowActionsMenu, which previously copy-pasted
 * this exact effect. Distinct from `useModalOverlay`, which locks body scroll
 * for full-screen modals — wrong for an inline popover.
 */
export function useDismissableMenu() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return { open, setOpen, containerRef };
}
